import { ComponentProps } from 'react';
import DatePicker from 'antd/es/date-picker';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';

import { DATE_TIME_FORMAT } from 'constants/Dates';

import './OpDatePicker.scss';

type DatePickerProps = ComponentProps<typeof DatePicker<Dayjs>>;

interface OpDatePickerProps
  extends Omit<DatePickerProps, 'value' | 'onChange' | 'picker'> {
  testId?: string;
  gtm?: string;
  value?: Dayjs | string;
  onChange?: (value: string | null, dateString: string | null) => void;
}

/**
 * OpDatePicker is a wrapper around Ant Design's DatePicker component.
 * It provides a date and time selection interface with string-based value handling for form compatibility.
 *
 * Use this component for selecting dates and times in forms. It automatically converts between
 * dayjs objects and ISO string format for easier form state management.
 *
 * @example
 * <OpDatePicker
 *   value={dateString}
 *   onChange={(isoString) => setDateString(isoString)}
 *   showTime
 * />
 *
 */
export const OpDatePicker = ({
  className = '',
  testId = 'op-date-picker',
  gtm,
  value,
  format = DATE_TIME_FORMAT,
  onChange,
  ...datePickerProps
}: OpDatePickerProps) => {
  // We want to store a string in the form values rather than a dayjs object
  const handleChange = (
    changedValue: Dayjs | null,
    dateString: string | null,
  ): void => {
    onChange?.(changedValue ? changedValue.toISOString() : null, dateString);
  };

  // We need to convert the now stored string to a dayjs object
  const pickerValue = typeof value === 'string' ? dayjs(value) : value;

  return (
    <DatePicker
      className={clsx('op-date-picker', className)}
      data-testid={testId}
      data-gtm={gtm}
      format={format}
      value={pickerValue}
      onChange={handleChange}
      {...datePickerProps}
    />
  );
};
